<template>
  <v-container v-if="question !== undefined">
    <v-row>
      <v-col offset-md="2">
        <h3>Edit Question - H{{ $route.params.id }}:Q{{ $route.params.key }}</h3>
      </v-col>
    </v-row>
    <v-row>
      <v-col md="2" xs="6" offset="2">
        <v-switch :input-value="question.open" color="primary" class="ma-2" label="Open"
          @change="toggleOpen(question.open)">
        </v-switch>
      </v-col>
      <v-col md="2" xs="6">
        <v-switch :input-value="question.correct" color="success" class="ma-2" label="Correct"
          @change="toggleCorrect(question.correct)">
        </v-switch>
      </v-col>
    </v-row>
    <v-row>
      <v-col md="2" xs="6" offset="2">
        <v-switch :input-value="question.isAudio" color="deep-purple lighten-1" class="ma-2" label="Audio Question"
          @change="toggleIsAudio(question.isAudio)">
        </v-switch>
      </v-col>
      <v-col md="2" xs="6">
        <v-switch :input-value="question.oneDone" color="deep-purple lighten-1" class="ma-2" label="One and Done"
          @change="toggleOneDone(question.oneDone)">
        </v-switch>
      </v-col>
    </v-row>
    <v-row>
      <v-col md="6" xs="12" offset="2">
        <v-container style="background-color: #373737; padding: 15px 30px; border-radius: 8px;">
          <v-row align="center" justify="space-between">
            <div>
              <v-icon large color="red" :disabled="isRecording" @click="startRecording()">
                mdi-record-circle
              </v-icon>
              <v-icon large color="blue" :disabled="!isRecording" @click="stopRecording()">
                mdi-stop-circle
              </v-icon>
              {{ audioRecorderStatus }}
            </div>
            <v-switch
              :input-value="transcribeEnable"
              color="primary"
              label="Transcribe"
              @change="toggleTranscribeEnable">
            </v-switch>
          </v-row>
        </v-container>
      </v-col>
    </v-row>
    <v-row>
      <v-col md="6" xs="12" offset-md="2">
        <v-text-field :value="question.pointValue" @change="updateQuestionPointValue($event)" label="Points" outlined
          autofocus></v-text-field>
      </v-col>
    </v-row>
    <v-row>
      <v-col md="6" xs="12" offset-md="2">
        <v-textarea outlined :value="question.text" @input="saveQuestionText" label="Question Text">
        </v-textarea>
      </v-col>
    </v-row>
    <v-row>
      <v-col md="6" xs="12" offset-md="2">
        <div class="row" v-if="question.audioUrl">
          <div class="audio-player">
            <audio :src="'https://storage.googleapis.com/mmpof3.appspot.com/audio/' + question.audioUrl" type="audio/mpeg" controls="controls"></audio>
          </div>
        </div>
      </v-col>
    </v-row>
    <v-row v-if="question.oneDone">
      <v-col col="6" offset-md="2">
        <v-switch :input-value="question.oneDoneSubmitted" color="deep-purple lighten-1" class="ma-2"
          label="1&D Submitted" @change="toggleOneDoneSubmitted(question.oneDoneSubmitted)">
        </v-switch>
      </v-col>
    </v-row>
    <v-row v-if="!question.open || question.oneDoneSubmitted">
      <v-col md="6" xs="12" offset="2" class="solution">
        <v-row>
          <v-col>
            <v-textarea outlined :value="question.solution.text" @change="updateQuestionSolution('text', $event)"
              :background-color="question.correct ? 'success' : ''" label="Solution Text">
            </v-textarea>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="6">
            <v-text-field :value="question.solution.submittedBy" label="Submitted By"
              @change="updateQuestionSolution('submittedBy', $event)" outlined></v-text-field>
            <span>{{ formatTime(question.solution.submittedAt) }}</span>
          </v-col>
          <v-col cols="6">
            <v-text-field :value="question.solution.calledInBy" label="Called In By"
              @change="updateQuestionSolution('calledInBy', $event)" outlined></v-text-field>
            <span>{{ formatTime(question.solution.closedAt) }}</span>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-text-field :value="question.solution.phoneBankWorker" label="Phone Bank Worker"
              @change="updateQuestionSolution('phoneBankWorker', $event)" outlined></v-text-field>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <v-row>
      <v-col md="6" xs="12" offset="2">
        <v-select v-model="numberSelected" :items="visualList" @change="attachVisual(numberSelected)" label="Visual #"
          outlined append-outer-icon="mdi-minus-circle" @click:append-outer="attachVisual(null)"></v-select>
      </v-col>
    </v-row>
    <v-row>
      <v-col md="6" xs="12" offset="2">
        <v-text-field :value="question.audioUrl" @change="updateQuestionAudioUrl($event)" label="Audio File"
          outlined></v-text-field>
      </v-col>
    </v-row>
    <v-row>
      <v-col md="6" xs="12" offset="2">
        <v-btn large color="primary darken-2" @click="finishEdit()">
          Finish
        </v-btn>
      </v-col>
    </v-row>
  </v-container>
  <v-container v-else>
    <v-skeleton-loader type="paragragh, card" />
  </v-container>
</template>

<script>
import { range, debounce, throttle } from 'lodash'

export default {
  data: () => ({
    hourNumber: 1,
    visible: true,
    visualList: range(1, 22),
    debouncer: null,
    throttler: null,
    numberSelected: null,
  }),
  beforeRouteEnter (to, from, next) {
    // just use `this`
    document.title = '[EDIT] Q' + to.params.key + ' | Meyerbase'
    next()
  },
  computed: {
    question: function () {
      return this.$store.getters['questions/getQuestion'](this.$route.params.id, this.$route.params.key)
    },
    questionKey: function () {
      return this.$store.getters['questions/getQuestionKey'](this.$route.params.id, this.$route.params.key)
    },
    audioRecorderStatus: function () {
      return this.$store.getters['audioRecorder/getRecorderStatus']()
    },
    isRecording: function () {
      return this.$store.getters['audioRecorder/getRecordingFlag']()
    },
    audioRecorder: function () {
      return this.$store.getters['audioRecorder']
    },
    transcribeEnable: function () {
      return this.$store.getters['audioRecorder/getTranscribeEnable']()
    },
  },
  mounted: async function () {
    await this.$store.dispatch('questions/bindHourQuestions', this.$route.params.id)
    await this.$store.dispatch('visuals/bindVisuals')
    await this.$store.dispatch('audioRecorder/bindAudioRecorder')
    await this.$store.dispatch('audioRecorder/isAudioRecorderOnline')

    this.numberSelected = this.question.visual ? this.question.visual.visualNumber : ''

    this.throttler = throttle(
      (newText) => {
        this.$store.dispatch('questions/saveQuestionText', {
          questionKey: this.questionKey,
          questionHour: this.question.hourNumber,
          questionText: newText,
        })
      },
      1500
    )
    this.debouncer = debounce(
      (newText) => {
        this.$store.dispatch('questions/saveQuestionText', {
          questionKey: this.questionKey,
          questionHour: this.question.hourNumber,
          questionText: newText,
        })
      },
      1000
    )

    // Run isAudioRecorderOnline every 12 seconds.
    this.recorderOnlineInterval = setInterval(() => {
      this.$store.dispatch('audioRecorder/isAudioRecorderOnline')
    }, 12000)
  },
  beforeDestroy () {
    // Clear the interval when the component is destroyed
    if (this.recorderOnlineInterval) {
      clearInterval(this.recorderOnlineInterval)
    }
  },
  methods: {
    saveQuestionText: function (change) {
      this.throttler(change)
      this.debouncer(change)
    },
    updateQuestionPointValue (points) {
      this.$store.dispatch('questions/updateQuestionPointValue', {
        hourNumber: this.$route.params.id,
        questionKey: this.questionKey,
        pointValue: parseInt(points),
      })
    },
    updateQuestionAudioUrl (url) {
      this.$store.dispatch('questions/updateQuestionAudioUrl', {
        hourNumber: this.$route.params.id,
        questionKey: this.questionKey,
        audioUrl: url,
      })
    },
    updateQuestionSolution (property, val) {
      this.$store.dispatch('questions/updateQuestionSolution', {
        hourNumber: this.$route.params.id,
        questionKey: this.questionKey,
        propertyName: property,
        value: val,
      })
    },
    finishEdit: function () {
      this.$router.push(`/hour/${this.$route.params.id}/question/${this.$route.params.key}`)
    },
    formatTime: function (timestamp) {
      let date = new Date(timestamp)
      return date.toLocaleString()
    },
    toggleOpen: function (isOpen) {
      this.$store.dispatch('questions/toggleOpen', {
        hourNumber: this.$route.params.id,
        questionKey: this.questionKey,
        open: !isOpen,
      })
    },
    toggleCorrect: function (isCorrect) {
      this.$store.dispatch('questions/toggleCorrect', {
        hourNumber: this.$route.params.id,
        questionKey: this.questionKey,
        correct: !isCorrect,
      })
    },
    toggleOneDone: function (isOneDone) {
      this.$store.dispatch('questions/toggleOneDone', {
        hourNumber: this.$route.params.id,
        questionKey: this.questionKey,
        oneDone: !isOneDone,
      })
    },
    toggleIsAudio: function (isAudio) {
      this.$store.dispatch('questions/toggleIsAudio', {
        hourNumber: this.$route.params.id,
        questionKey: this.questionKey,
        isAudio: !isAudio,
      })
    },
    toggleOneDoneSubmitted: function (isSubmitted) {
      this.$store.dispatch('questions/toggleOneDoneSubmitted', {
        hourNumber: this.$route.params.id,
        questionKey: this.questionKey,
        oneDoneSubmitted: !isSubmitted,
      })
    },
    startRecording: function () {
      this.$store.dispatch('audioRecorder/startRecording', {
        hourNumber: parseInt(this.$route.params.id),
        questionNumber: parseInt(this.$route.params.key),
      })
    },
    stopRecording: function () {
      this.$store.dispatch('audioRecorder/stopRecording', {
      })
    },
    attachVisual: function (visualNumber) {
      let visual
      if (visualNumber) {
        visual = this.$store.getters['visuals/getVisualByNumber'](visualNumber)
      } else {
        visual = null
        this.numberSelected = null
      }

      this.$store.dispatch('questions/updateVisualOnQuestion', {
        hourNumber: this.$route.params.id,
        questionKey: this.questionKey,
        visual: visual,
      })
    },
    toggleTranscribeEnable () {
      const currentValue = this.transcribeEnable
      this.$store.dispatch('audioRecorder/setTranscribeEnable', !currentValue)
    },
  },
}
</script>

<style scoped>
.solution {
  background-color: success;
}
</style>
